import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useLeague } from '@/contexts/LeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { Users, Bomb, Trophy, Clock, ChevronRight } from 'lucide-react';
import MLBBetaTransactionHistory from './MLBBetaTransactionHistory';
import InlineLeagueChat from '@/components/chat/InlineLeagueChat';

import LeagueAnnouncementBanner from './LeagueAnnouncementBanner';

interface CurrentMatchup {
  id: string;
  week: number;
  status: string;
  home_team_id: string;
  home_team_name: string;
  home_score: number;
  away_team_id: string;
  away_team_name: string;
  away_score: number;
}

interface MLBBetaDashboardProps {
  onNavigate: (tab: 'dashboard' | 'schedule' | 'leaderboard' | 'players' | 'matchups' | 'sabotage' | 'standings' | 'fantasy-schedule' | 'settings') => void;
  draftRoomUrl?: string;
}

const MLBBetaDashboard = ({ onNavigate, draftRoomUrl }: MLBBetaDashboardProps) => {
  const [loading, setLoading] = useState(true);
  const [teamCount, setTeamCount] = useState(0);
  const [sabotageCount, setSabotageCount] = useState(0);
  const [sabotageRevenue, setSabotageRevenue] = useState(0);
  const [currentWeek, setCurrentWeek] = useState(1);
  const [myMatchup, setMyMatchup] = useState<CurrentMatchup | null>(null);
  const [myTeamId, setMyTeamId] = useState<string | null>(null);
  const [draftScheduledFor, setDraftScheduledFor] = useState<string | null>(null);
  const [draftStatus, setDraftStatus] = useState<string | null>(null);
  const [countdown, setCountdown] = useState('');
  const { activeLeague } = useLeague();
  const { user } = useAuth();

  useEffect(() => {
    if (!activeLeague?.id || !user?.id) return;

    const fetchDashboard = async () => {
      try {
        const [teamsRes, sabotagesRes, leagueRes, myTeamRes, draftRes] = await Promise.all([
          supabase.from('teams').select('id', { count: 'exact', head: true }).eq('league_id', activeLeague.id),
          supabase.from('applied_sabotages').select('id', { count: 'exact', head: true }).eq('league_id', activeLeague.id),
          supabase.from('leagues').select('sabotage_revenue').eq('id', activeLeague.id).single(),
          supabase.from('teams').select('id').eq('league_id', activeLeague.id).eq('user_id', user.id).maybeSingle(),
          supabase.from('drafts').select('id').eq('league_id', activeLeague.id).limit(1).maybeSingle(),
        ]);

        // Count draft sabotages too
        let draftSabotageCount = 0;
        if (draftRes.data?.id) {
          const { count } = await supabase
            .from('draft_applied_sabotages')
            .select('id', { count: 'exact', head: true })
            .eq('draft_id', draftRes.data.id);
          draftSabotageCount = count || 0;
        }

        setTeamCount(teamsRes.count || 0);
        setSabotageCount((sabotagesRes.count || 0) + draftSabotageCount);
        setSabotageRevenue(leagueRes.data?.sabotage_revenue || 0);

        const userTeamId = myTeamRes.data?.id || null;
        setMyTeamId(userTeamId);

        if (userTeamId) {
          const { data: matchups } = await supabase
            .from('matchups')
            .select('*')
            .eq('league_id', activeLeague.id)
            .or(`home_team_id.eq.${userTeamId},away_team_id.eq.${userTeamId}`)
            .in('status', ['scheduled', 'live', 'in_progress'])
            .order('week', { ascending: true })
            .limit(1);

          if (matchups && matchups.length > 0) {
            setMyMatchup(matchups[0]);
            setCurrentWeek(matchups[0].week);
          } else {
            const { data: completed } = await supabase
              .from('matchups')
              .select('*')
              .eq('league_id', activeLeague.id)
              .or(`home_team_id.eq.${userTeamId},away_team_id.eq.${userTeamId}`)
              .order('week', { ascending: false })
              .limit(1);
            if (completed && completed.length > 0) {
              setMyMatchup(completed[0]);
              setCurrentWeek(completed[0].week);
            }
          }
        }
        // Fetch draft info
        const { data: draftData } = await supabase
          .from('drafts')
          .select('status, scheduled_for')
          .eq('league_id', activeLeague.id)
          .maybeSingle();

        if (draftData) {
          setDraftStatus(draftData.status);
          setDraftScheduledFor(draftData.scheduled_for);
        }
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [activeLeague?.id, user?.id]);

  // Countdown timer for scheduled draft
  const getCountdownText = useCallback(() => {
    if (!draftScheduledFor) return '';
    const diff = new Date(draftScheduledFor).getTime() - Date.now();
    if (diff <= 0) return 'Starting soon!';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }, [draftScheduledFor]);

  useEffect(() => {
    if (!draftScheduledFor || draftStatus !== 'scheduled') return;
    const diff = new Date(draftScheduledFor).getTime() - Date.now();
    if (diff <= 0 || diff > 24 * 60 * 60 * 1000) return;

    setCountdown(getCountdownText());
    const interval = setInterval(() => setCountdown(getCountdownText()), 30_000);
    return () => clearInterval(interval);
  }, [draftScheduledFor, draftStatus, getCountdownText]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const isMyHome = myMatchup && myTeamId === myMatchup.home_team_id;
  const myScore = myMatchup ? (isMyHome ? myMatchup.home_score : myMatchup.away_score) : 0;
  const oppScore = myMatchup ? (isMyHome ? myMatchup.away_score : myMatchup.home_score) : 0;
  const myName = myMatchup ? (isMyHome ? myMatchup.home_team_name : myMatchup.away_team_name) : '';
  const oppName = myMatchup ? (isMyHome ? myMatchup.away_team_name : myMatchup.home_team_name) : '';
  const matchupStatus = myMatchup?.status || 'scheduled';

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      <LeagueAnnouncementBanner />

      {/* Draft Countdown Banner */}
      {draftStatus === 'scheduled' && draftScheduledFor && countdown && (
        <Card className="border-primary/40 bg-primary/10">
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className="w-5 h-5 text-primary shrink-0" />
            <div>
              <p className="text-sm font-semibold text-foreground">
                ⚾ Draft starts in {countdown}
              </p>
              <p className="text-xs text-muted-foreground">
                {new Date(draftScheduledFor).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })} at {new Date(draftScheduledFor).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Draft In Progress Banner */}
      {draftStatus === 'in_progress' && (
        draftRoomUrl ? (
          <a href={draftRoomUrl} target="_blank" rel="noopener noreferrer" className="block">
            <Card className="border-green-500/40 bg-green-500/10 cursor-pointer">
              <CardContent className="p-4 flex items-center gap-3">
                <span className="text-lg">⚾</span>
                <div>
                  <p className="text-sm font-semibold text-foreground">Draft is LIVE!</p>
                  <p className="text-xs text-muted-foreground">Click to open the draft room</p>
                </div>
              </CardContent>
            </Card>
          </a>
        ) : (
          <Card className="border-green-500/40 bg-green-500/10 cursor-pointer" onClick={() => onNavigate('draft' as any)}>
            <CardContent className="p-4 flex items-center gap-3">
              <span className="text-lg">⚾</span>
              <div>
                <p className="text-sm font-semibold text-foreground">Draft is LIVE!</p>
                <p className="text-xs text-muted-foreground">Tap to join the draft room</p>
              </div>
            </CardContent>
          </Card>
        )
      )}

      {/* Welcome Hero */}
      <Card className="bg-card border-border overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm text-muted-foreground">📅 WEEK {currentWeek}</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground">
            Welcome to <span className="text-primary">Sabotage</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Where strikeouts are home runs and chaos reigns supreme.
          </p>

          {/* Stats — MLB-028: PRIZE POOL is the hero (real money on the line); other counts demoted to a secondary row; SABOTAGE REV dropped from the user-facing dashboard (admin metric only) */}
          <div className="mt-5 space-y-3">
            <div className="rounded-lg bg-muted/40 border border-border p-4 flex items-center gap-3">
              <Trophy className="w-7 h-7 text-yellow-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Prize Pool</p>
                <p className="text-3xl md:text-4xl font-bold font-heading text-yellow-400 tabular-nums leading-none">
                  ${teamCount * 100 + sabotageRevenue}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <StatPill icon={<Users className="w-4 h-4 text-muted-foreground" />} value={teamCount} label="Teams" onClick={() => onNavigate('standings')} />
              <StatPill icon={<Bomb className="w-4 h-4 text-muted-foreground" />} value={sabotageCount} label="Sabotages" onClick={() => onNavigate('sabotage')} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Your Matchup */}
      {myMatchup ? (
        <Card
          /* MLB-029 — entire card is tappable (already was), but pulse a primary ring while LIVE so the card matches the badge's energy; chevron + "Tap to watch live" subtext makes the affordance unambiguous */
          className={`bg-card cursor-pointer hover:border-primary/30 transition-colors overflow-hidden ${(matchupStatus === 'live' || matchupStatus === 'in_progress') ? 'ring-2 ring-primary border-primary animate-pulse' : 'border-border'}`}
          onClick={() => onNavigate('matchups')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold font-heading text-foreground flex items-center gap-2">
                ⚔️ Your Matchup
              </h2>
              <Badge
                variant={matchupStatus === 'live' || matchupStatus === 'in_progress' ? 'destructive' : 'secondary'}
                className="text-[10px] px-2 py-0.5"
              >
                {matchupStatus === 'live' || matchupStatus === 'in_progress' ? '● LIVE' : matchupStatus === 'final' ? 'FINAL' : `WEEK ${myMatchup.week}`}
              </Badge>
            </div>

            <div className="flex items-center">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground truncate">{myName || 'Your Team'}</p>
                <p className="text-2xl font-bold font-heading text-foreground">{myScore.toFixed(1)}</p>
                <p className="text-xs text-primary">You</p>
              </div>

              <div className="shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground mx-2">vs</div>

              <div className="flex-1 min-w-0 text-right">
                <p className="text-xs text-muted-foreground truncate">{oppName || 'Opponent'}</p>
                <p className="text-2xl font-bold font-heading text-foreground">{oppScore.toFixed(1)}</p>
                <p className="text-xs text-muted-foreground">Opponent</p>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-center gap-1 text-xs text-primary font-medium">
              <span>{matchupStatus === 'live' || matchupStatus === 'in_progress' ? 'Tap to watch live' : 'View full matchup'}</span>
              <ChevronRight className="w-3 h-3" />
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-card border-border">
          <CardContent className="p-6 text-center">
            <p className="text-sm text-muted-foreground">No matchups scheduled yet. Generate the schedule from Settings.</p>
          </CardContent>
        </Card>
      )}

      {/* Transaction History */}
      <MLBBetaTransactionHistory />

      {/* Inline League Chat */}
      <InlineLeagueChat />

    </div>
  );
};

const StatPill = ({ icon, value, label, onClick }: { icon: React.ReactNode; value: number | string; label: string; onClick?: () => void }) => (
  <div
    className={`flex items-center gap-2.5 p-3 rounded-lg bg-muted/40 border border-border ${onClick ? 'cursor-pointer hover:border-primary/30 transition-colors' : ''}`}
    onClick={onClick}
  >
    {icon}
    <div>
      <p className="text-lg font-bold font-heading text-foreground">{value}</p>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
    </div>
  </div>
);

export default MLBBetaDashboard;
